import { useCallback, useEffect, useRef, useState } from 'react';
import type { MediaProfile } from '@shared/protocol';
import { settingsFor } from './mediaProfile';

export interface DeviceChoice {
  audioInput?: string;
  videoInput?: string;
  /** Output sink. Not a getUserMedia constraint — changing it must never
      re-acquire the camera, so it is deliberately absent from the capture
      effect's dependencies. */
  audioOutput?: string;
}

export interface LocalMedia {
  stream: MediaStream | null;
  devices: MediaDeviceInfo[];
  choice: DeviceChoice;
  audioEnabled: boolean;
  videoEnabled: boolean;
  error: string | null;
  /** True until the first getUserMedia attempt has settled. */
  starting: boolean;
  setAudioEnabled: (on: boolean) => void;
  setVideoEnabled: (on: boolean) => void;
  selectDevice: (kind: keyof DeviceChoice, deviceId: string) => void;
  retry: () => void;
  stop: () => void;
}

/** Every device choice is remembered between visits. See `restore` for what
    happens when a remembered device is gone by the next visit. */
const STORE_KEYS: Record<keyof DeviceChoice, string> = {
  audioInput: 'vortex.mic',
  videoInput: 'vortex.camera',
  audioOutput: 'vortex.speaker',
};

function restore(): DeviceChoice {
  const choice: DeviceChoice = {};
  for (const kind of Object.keys(STORE_KEYS) as (keyof DeviceChoice)[]) {
    const id = localStorage.getItem(STORE_KEYS[kind]);
    if (id) choice[kind] = id;
  }
  return choice;
}

function forgetInputs() {
  localStorage.removeItem(STORE_KEYS.audioInput);
  localStorage.removeItem(STORE_KEYS.videoInput);
}

/** Chrome and Firefox implement setSinkId; Safari and most mobile do not. */
export function canChooseSpeaker(): boolean {
  return typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;
}

/**
 * Route a media element to the chosen speaker. A stored sink can disappear
 * between sessions (headphones unplugged), so a failure here falls back to the
 * default output rather than surfacing as a broken tile.
 */
export async function applySink(el: HTMLMediaElement, deviceId: string | undefined): Promise<void> {
  if (!deviceId || !canChooseSpeaker()) return;
  try {
    await (el as HTMLMediaElement & { setSinkId(id: string): Promise<void> }).setSinkId(deviceId);
  } catch {
    /* keep the default output */
  }
}

function describe(err: unknown): string {
  const name = err instanceof DOMException ? err.name : '';
  switch (name) {
    case 'NotAllowedError':
      return 'Camera and microphone access was blocked. Allow it in your browser’s site settings, then retry.';
    case 'NotFoundError':
      return 'No camera or microphone was found.';
    case 'NotReadableError':
      return 'Your camera or microphone is already in use by another application.';
    case 'OverconstrainedError':
      return 'The selected device is no longer available.';
    case 'SecurityError':
      return 'Media capture needs a secure connection (https).';
    default:
      return err instanceof Error ? err.message : 'Could not start your camera and microphone.';
  }
}

/** A remembered device that has since been unplugged. */
function isMissingDevice(err: unknown): boolean {
  const name = err instanceof DOMException ? err.name : '';
  return name === 'OverconstrainedError' || name === 'NotFoundError';
}

/**
 * Constraints come from the meeting's profile; only the device pin is decided
 * here. Everything the profile asks for is `ideal` rather than `exact`, so a
 * webcam that cannot do 1080p gives its best instead of failing the call —
 * except the device id, which is exact by design, because silently switching
 * someone to a different microphone is worse than telling them theirs is gone.
 */
function constraintsFor(
  part: MediaTrackConstraints,
  deviceId?: string,
): MediaTrackConstraints {
  return deviceId ? { deviceId: { exact: deviceId }, ...part } : part;
}

/**
 * Owns the local capture stream: permissions, device selection, and the
 * enabled/disabled state of each track.
 *
 * Turning the camera off **stops** the video track rather than flipping
 * `track.enabled`. Flipping enabled keeps the capture device open, which leaves
 * the camera indicator light on — and that light is the one signal people
 * actually trust about whether they are being watched, so it has to go out.
 * Coming back on re-acquires video only, leaving the microphone untouched.
 *
 * Muting the microphone still flips `enabled`: there is no comparable hardware
 * indicator to honour, and an unmute that has to re-acquire clips the first
 * word of whatever is being said.
 */
export function useLocalMedia(active: boolean, profile?: MediaProfile): LocalMedia {
  const settings = settingsFor(profile);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [choice, setChoice] = useState<DeviceChoice>(restore);
  const [audioEnabled, setAudio] = useState(true);
  const [videoEnabled, setVideo] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(active);
  const [attempt, setAttempt] = useState(0);

  const streamRef = useRef<MediaStream | null>(null);
  // Read by the async paths, which must not re-run when these change.
  const activeRef = useRef(active);
  const videoWanted = useRef(true);
  const choiceRef = useRef(choice);
  const cameraRun = useRef(0);
  const settingsRef = useRef(settings);

  activeRef.current = active;
  choiceRef.current = choice;
  settingsRef.current = settings;

  /* Consumers key off the stream's identity — useBackground restarts its
     processor on a new one, useRoomMedia diffs what it publishes — so a change
     to the track list is published as a new MediaStream, never as a mutation of
     the existing one. */
  const publish = useCallback((tracks: MediaStreamTrack[]) => {
    const next = new MediaStream(tracks);
    streamRef.current = next;
    setStream(next);
  }, []);

  const stop = useCallback(() => {
    cameraRun.current++;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setStream(null);
  }, []);

  useEffect(() => {
    if (!active) return;

    let cancelled = false;
    setStarting(true);

    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new DOMException('getUserMedia unavailable', 'SecurityError');
        }

        // Only ask for the camera if it is meant to be on: arriving with it off
        // must not light it up for the instant it takes to stop the track.
        const wantVideo = videoWanted.current;
        const ask = (pinned: boolean) =>
          navigator.mediaDevices.getUserMedia({
            audio: constraintsFor(settings.audio, pinned ? choice.audioInput : undefined),
            video: wantVideo
              ? constraintsFor(settings.video, pinned ? choice.videoInput : undefined)
              : false,
          });

        let next: MediaStream;
        try {
          next = await ask(true);
        } catch (err) {
          // A remembered mic or camera can be unplugged by the next visit.
          // Falling back to the defaults beats greeting someone with an error
          // about a device they have forgotten they ever chose.
          if (!isMissingDevice(err) || (!choice.audioInput && !choice.videoInput)) throw err;
          next = await ask(false);
          if (!cancelled) {
            forgetInputs();
            setChoice((prev) => ({ ...prev, audioInput: undefined, videoInput: undefined }));
          }
        }

        if (cancelled) {
          next.getTracks().forEach((t) => t.stop());
          return;
        }

        // Swap only after the new stream is live, so the preview never blanks.
        streamRef.current?.getTracks().forEach((t) => t.stop());
        next.getAudioTracks().forEach((t) => (t.enabled = audioEnabled));
        publish(next.getTracks());
        setError(null);

        // Labels are empty until permission is granted, so enumerate after.
        setDevices(await navigator.mediaDevices.enumerateDevices());
      } catch (err) {
        if (!cancelled) {
          setError(describe(err));
          streamRef.current = null;
          setStream(null);
        }
      } finally {
        if (!cancelled) setStarting(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // audioEnabled/videoEnabled are applied imperatively below, not by re-running.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, choice.audioInput, choice.videoInput, attempt, publish, settings]);

  useEffect(() => () => stop(), [stop]);

  /**
   * Enumerate immediately and on every device change, independently of whether
   * capture succeeded.
   *
   * Enumerating only after a successful getUserMedia — as this used to — left
   * anyone who denied permission, had no webcam, or joined purely to listen
   * with no speaker picker at all, even though choosing an output device needs
   * no permission. Labels are blank until permission is granted, which the
   * pickers already fall back for; the capture path re-enumerates afterwards to
   * fill them in.
   */
  useEffect(() => {
    if (!active || !navigator.mediaDevices) return;
    const enumerate = () => {
      navigator.mediaDevices.enumerateDevices().then(setDevices).catch(() => {});
    };
    enumerate();
    navigator.mediaDevices.addEventListener('devicechange', enumerate);
    return () => navigator.mediaDevices.removeEventListener('devicechange', enumerate);
  }, [active]);

  const setAudioEnabled = useCallback((on: boolean) => {
    setAudio(on);
    streamRef.current?.getAudioTracks().forEach((t) => (t.enabled = on));
  }, []);

  const setVideoEnabled = useCallback(
    (on: boolean) => {
      setVideo(on);
      videoWanted.current = on;
      // Guards a slow re-acquire against a later toggle overtaking it.
      const run = ++cameraRun.current;

      if (!on) {
        const current = streamRef.current;
        const video = current?.getVideoTracks() ?? [];
        if (!current || video.length === 0) return;
        video.forEach((t) => t.stop());
        publish(current.getAudioTracks());
        return;
      }

      if (!activeRef.current || streamRef.current?.getVideoTracks().length) return;

      (async () => {
        try {
          const fresh = await navigator.mediaDevices.getUserMedia({
            video: constraintsFor(settingsRef.current.video, choiceRef.current.videoInput),
          });
          const stale = cameraRun.current !== run || !videoWanted.current || !activeRef.current;
          if (stale) {
            fresh.getTracks().forEach((t) => t.stop());
            return;
          }
          publish([...fresh.getVideoTracks(), ...(streamRef.current?.getAudioTracks() ?? [])]);
          setError(null);
        } catch (err) {
          if (cameraRun.current !== run) return;
          // The camera did not come back — say so, and leave the button off
          // rather than showing "on" over a dead tile.
          videoWanted.current = false;
          setVideo(false);
          setError(describe(err));
        }
      })();
    },
    [publish],
  );

  const selectDevice = useCallback((kind: keyof DeviceChoice, deviceId: string) => {
    setChoice((prev) => ({ ...prev, [kind]: deviceId || undefined }));
    if (deviceId) localStorage.setItem(STORE_KEYS[kind], deviceId);
    else localStorage.removeItem(STORE_KEYS[kind]);
  }, []);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return {
    stream,
    devices,
    choice,
    audioEnabled,
    videoEnabled,
    error,
    starting,
    setAudioEnabled,
    setVideoEnabled,
    selectDevice,
    retry,
    stop,
  };
}
